// Versão: 1.09
// Data..: 2026-10-13
// Roteamento principal do SPA, com rotas protegidas por autenticação.
// v1.09: "Tarefas" unificado como aba em /schedule; a rota /tasks virou redirect para
// /schedule (mantém bookmarks) e a antiga TasksPage foi removida.
// v1.08: Ponto Eletrônico — rota /timeclock (guard RequireRole ADMIN/AGENT/ACCOUNTING);
// o papel ACCOUNTING cai em /timeclock ao abrir a raiz '/'.
// v1.07: removida a rota /calendar; o calendário Zimbra passou para dentro de /schedule.
// v1.03: rota /finance (Financeiro API, admin) — menu próprio no grupo Gestão.
// v1.04: rota pública /portal/sign/:token (assinatura eletrônica de contrato).
// v1.05: rota pública /portal/pagar/:token (link de pagamento).

import { Routes, Route, Navigate } from 'react-router-dom';
import { UserRole } from '@makweb/shared';
import { useAuth } from './context/AuthContext.js';
import { Layout } from './components/Layout.js';
import { RequireRole } from './components/RequireRole.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { TicketsPage } from './pages/TicketsPage.js';
import { TicketDetailPage } from './pages/TicketDetailPage.js';
import { NewTicketPage } from './pages/NewTicketPage.js';
import { MyCentralPage } from './pages/MyCentralPage.js';
import { ManagementPage } from './pages/ManagementPage.js';
import { FinancePage } from './pages/FinancePage.js';
import { KnowledgePage } from './pages/KnowledgePage.js';
import { AdminPage } from './pages/AdminPage.js';
import { AccountPage } from './pages/AccountPage.js';
import { TimeclockPage } from './pages/TimeclockPage.js';
import { SchedulePage } from './pages/SchedulePage.js';
import { WhatsappPage } from './pages/WhatsappPage.js';
import { PublicHomePage } from './pages/public/PublicHomePage.js';
import { PublicArticlePage } from './pages/public/PublicArticlePage.js';
import { PublicOpenTicketPage } from './pages/public/PublicOpenTicketPage.js';
import { PublicSurveyPage } from './pages/public/PublicSurveyPage.js';
import { PublicSignPage } from './pages/public/PublicSignPage.js';
import { PublicPaymentPage } from './pages/public/PublicPaymentPage.js';

export function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh' }}>
        <span className="spinner" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Portal público — acessível sem login */}
      <Route path="/portal" element={<PublicHomePage />} />
      <Route path="/portal/kb/:slug" element={<PublicArticlePage />} />
      <Route path="/portal/abrir" element={<PublicOpenTicketPage />} />
      <Route path="/portal/survey/:token" element={<PublicSurveyPage />} />
      <Route path="/portal/sign/:token" element={<PublicSignPage />} />
      <Route path="/portal/pagar/:token" element={<PublicPaymentPage />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />

      {/* Área autenticada */}
      {user ? (
        <>
          {/* Raiz: a contabilidade só enxerga o Ponto, então cai direto em /timeclock. */}
          <Route
            path="/"
            element={user.role === UserRole.ACCOUNTING ? <Navigate to="/timeclock" replace /> : <Layout><DashboardPage /></Layout>}
          />
          <Route
            path="/timeclock"
            element={
              <RequireRole roles={[UserRole.ADMIN, UserRole.AGENT, UserRole.ACCOUNTING]}>
                <Layout><TimeclockPage /></Layout>
              </RequireRole>
            }
          />
          <Route path="/central" element={<Layout><MyCentralPage /></Layout>} />
          <Route path="/management" element={<Layout><ManagementPage /></Layout>} />
          <Route path="/finance" element={<Layout><FinancePage /></Layout>} />
          <Route path="/tickets" element={<Layout><TicketsPage /></Layout>} />
          <Route path="/tickets/new" element={<Layout><NewTicketPage /></Layout>} />
          <Route path="/tickets/:id" element={<Layout><TicketDetailPage /></Layout>} />
          {/* Tarefas virou aba em Agendamentos; mantém o redirect para não quebrar bookmarks. */}
          <Route path="/tasks" element={<Navigate to="/schedule" replace />} />
          <Route path="/schedule" element={<Layout><SchedulePage /></Layout>} />
          <Route path="/whatsapp" element={<Layout><WhatsappPage /></Layout>} />
          <Route path="/kb" element={<Layout><KnowledgePage /></Layout>} />
          <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
          <Route path="/account" element={<Layout><AccountPage /></Layout>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        // Não autenticado: qualquer rota interna cai no portal público.
        <Route path="*" element={<Navigate to="/portal" replace />} />
      )}
    </Routes>
  );
}
